import React, { useState, useRef, useEffect, useCallback } from 'react';

const PickerWheel = ({ items, defaultIndex = 0, onChange, width = 80 }) => {
  const [currentIndex, setCurrentIndex] = useState(defaultIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [transform, setTransform] = useState(0);

  const wheelRef = useRef(null);
  const dragStateRef = useRef({
    startY: 0,
    startTransform: 0,
    velocity: 0,
    lastY: 0,
    lastTime: 0,
  });

  const itemHeight = 40;

  // 위치 업데이트
  const updatePosition = useCallback(
    (index) => {
      const newTransform = -index * itemHeight;
      setTransform(newTransform);
      setCurrentIndex(index);
      if (onChange) {
        onChange(items[index], index);
      }
    },
    [items, onChange, itemHeight]
  );

  // 가장 가까운 아이템으로 스냅
  const snapToNearestItem = useCallback(
    (targetTransform) => {
      const index = Math.round(-targetTransform / itemHeight);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
      updatePosition(clampedIndex);
    },
    [items.length, itemHeight, updatePosition]
  );

  // 드래그 시작
  const handleStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(true);

      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dragState = dragStateRef.current;

      dragState.startY = clientY;
      dragState.lastY = clientY;
      dragState.lastTime = Date.now();
      dragState.velocity = 0;
      dragState.startTransform = transform;
    },
    [transform]
  );

  // 드래그 중
  const handleMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();

      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dragState = dragStateRef.current;
      const deltaY = clientY - dragState.startY;
      const newTransform = dragState.startTransform + deltaY;

      // 속도 계산
      const now = Date.now();
      const timeDelta = now - dragState.lastTime;
      if (timeDelta > 0) {
        dragState.velocity = (clientY - dragState.lastY) / timeDelta;
      }
      dragState.lastY = clientY;
      dragState.lastTime = now;

      setTransform(newTransform);
    },
    [isDragging]
  );

  // 드래그 끝
  const handleEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const dragState = dragStateRef.current;
    const inertiaDistance = dragState.velocity * 200;
    const targetTransform = transform + inertiaDistance;

    snapToNearestItem(targetTransform);
  }, [isDragging, transform, snapToNearestItem]);

  // 휠 이벤트
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1 : -1;
      const newIndex = Math.max(
        0,
        Math.min(items.length - 1, currentIndex + delta)
      );
      updatePosition(newIndex);
    },
    [currentIndex, items.length, updatePosition]
  );

  // 클릭 이벤트
  const handleClick = useCallback(
    (e) => {
      const item = e.target.closest('[data-index]');
      if (item) {
        const index = parseInt(item.dataset.index);
        updatePosition(index);
      }
    },
    [updatePosition]
  );

  // 이벤트 리스너 등록
  useEffect(() => {
    const handleMouseMove = (e) => handleMove(e);
    const handleMouseUp = (e) => handleEnd(e);
    const handleTouchMove = (e) => handleMove(e);
    const handleTouchEnd = (e) => handleEnd(e);

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  // 초기 위치 설정
  useEffect(() => {
    updatePosition(defaultIndex);
  }, []);

  useEffect(() => {
    const node = wheelRef.current;
    if (!node) return;

    node.addEventListener('touchstart', handleStart, { passive: false });

    return () => {
      node.removeEventListener('touchstart', handleStart);
    };
  }, [handleStart]);

  return (
    <div
      style={{
        position: 'relative',
        width: `${width}px`,
        height: '200px',
        overflow: 'hidden',
        borderRadius: '8px',
        background: '#f8f8f8',
      }}
    >
      <div
        ref={wheelRef}
        style={{
          transform: `translateY(${transform}px)`,
          transition: isDragging
            ? 'none'
            : 'transform 0.3s cubic-bezier(0.17, 0.04, 0.03, 0.94)',
          position: 'relative',
          padding: '80px 0',
        }}
        onMouseDown={handleStart}
        onWheel={handleWheel}
        onClick={handleClick}
      >
        {items.map((item, index) => {
          const isActive = index === Math.round(-transform / itemHeight);
          return (
            <div
              key={index}
              data-index={index}
              style={{
                height: `${itemHeight}px`,
                lineHeight: `${itemHeight}px`,
                textAlign: 'center',
                fontSize: isActive ? '20px' : '18px',
                fontWeight: isActive ? '600' : '400',
                color: isActive ? '#007AFF' : '#666',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
            >
              {item}
            </div>
          );
        })}
      </div>

      {/* 오버레이 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          background: `linear-gradient(
            to bottom,
            rgba(248,248,248,0.9) 0%,
            transparent 30%,
            transparent 70%,
            rgba(248,248,248,0.9) 100%
          )`,
        }}
      />

      {/* 선택 영역 표시 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: `${itemHeight}px`,
          marginTop: `-${itemHeight / 2}px`,
          borderTop: '1px solid #ddd',
          borderBottom: '1px solid #ddd',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

// ✅ 부모에서 value 내려주고 onChange로 다시 올려줌
const TimePicker = ({ value, onChange }) => {
  const selectedTime = value || {
    hour: '12',
    minute: '00',
    period: 'AM',
  };

  const hours = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, '0')
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, '0')
  );
  const periods = ['AM', 'PM'];

  const handleTimeChange = (type, val) => {
    const updated = { ...selectedTime, [type]: val };
    const formatted = `${updated.hour}:${updated.minute} ${updated.period}`;
    onChange?.(formatted, updated); // 부모로 올려줌
  };

  return (
    <div
      style={{
        minHeight: '40%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          gap: '20px',
          userSelect: 'none',
        }}
      >
        {/* 시간 휠 */}
        <PickerWheel
          items={hours}
          defaultIndex={hours.indexOf(selectedTime.hour)}
          onChange={(value) => handleTimeChange('hour', value)}
          width={80}
        />

        {/* 구분자 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#007AFF',
          }}
        >
          :
        </div>

        {/* 분 휠 */}
        <PickerWheel
          items={minutes}
          defaultIndex={minutes.indexOf(selectedTime.minute)}
          onChange={(value) => handleTimeChange('minute', value)}
          width={80}
        />

        {/* AM/PM 휠 */}
        <PickerWheel
          items={periods}
          defaultIndex={periods.indexOf(selectedTime.period)}
          onChange={(value) => handleTimeChange('period', value)}
          width={60}
        />
      </div>
    </div>
  );
};

export default TimePicker;
